import { ZoneType } from "@fairfleet/geotab";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Tooltip from "@mui/material/Tooltip";
import { useTranslation } from "react-i18next";
import { colorToCss } from "../utils/zoneColor";
import { isBuiltInZoneType } from "../utils/zoneDraft";
import { zoneTypeLabel } from "../utils/zoneTypes";
import ColorDot from "./ColorDot";

interface Props {
  types: ZoneType[];
  counts: Map<string, number>;
  onEdit(type: ZoneType): void;
  onCreate(): void;
  onRemove(type: ZoneType): void;
  onShowZones(typeId: string): void;
}

export default function ZoneTypeList({ types, counts, onEdit, onCreate, onRemove, onShowZones }: Props) {
  const { t } = useTranslation();

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0, flex: 1 }}>
      <Box sx={{ p: 1 }}>
        <Button startIcon={<AddIcon />} variant="contained" size="small" onClick={onCreate}>
          {t("zonesPage.types.create")}
        </Button>
      </Box>
      <List dense sx={{ overflowY: "auto", flex: 1 }}>
        {types.map(type => {
          const label = zoneTypeLabel(type, t);
          const count = counts.get(type.id) ?? 0;
          const builtIn = isBuiltInZoneType(type);
          return (
            <ListItem
              key={type.id}
              secondaryAction={
                builtIn ? (
                  <Tooltip title={t("zonesPage.types.builtIn")}>
                    <LockOutlinedIcon fontSize="small" color="disabled" />
                  </Tooltip>
                ) : (
                  <>
                    <IconButton
                      size="small"
                      aria-label={`${t("zonesPage.types.edit")} ${label}`}
                      onClick={() => onEdit(type)}
                    >
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      aria-label={`${t("zonesPage.types.delete")} ${label}`}
                      disabled={count > 0}
                      onClick={() => onRemove(type)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </>
                )
              }
            >
              <ColorDot color={colorToCss(type.fillColor ?? { r: 158, g: 158, b: 158, a: 255 })} />
              <ListItemText
                primary={label}
                secondary={
                  count > 0 ? (
                    <Link component="button" variant="caption" onClick={() => onShowZones(type.id)}>
                      {t("zonesPage.types.inUse", { count })}
                    </Link>
                  ) : (
                    t("zonesPage.types.unused")
                  )
                }
              />
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
}
